import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || 'https://api.jaychrisedi.com';
  console.log('API_URL:', API_URL); // Debug log

  // Load user from localStorage on mount
  // Also handle auto-login from Shopify OAuth redirect (?shopify_token=xxx)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shopifyToken = params.get('shopify_token');

    if (shopifyToken) {
      // Decode the JWT to get user/org info (no signature check needed client-side)
      try {
        const payload = JSON.parse(atob(shopifyToken.split('.')[1]));
        const autoUser = { id: payload.userId, email: payload.email, role: payload.role };
        const autoOrg  = { id: payload.organizationId };
        localStorage.setItem('token', shopifyToken);
        localStorage.setItem('user', JSON.stringify(autoUser));
        localStorage.setItem('organization', JSON.stringify(autoOrg));
        setToken(shopifyToken);
        setUser(autoUser);
        setOrganization(autoOrg);
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
        setLoading(false);
        return;
      } catch (e) {
        console.error('Failed to parse shopify_token:', e);
      }
    }

    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedOrg = localStorage.getItem('organization');

    if (storedToken && storedUser && storedOrg) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      setOrganization(JSON.parse(storedOrg));
    }
    setLoading(false);
  }, []);

  const register = async (email, password, firstName, lastName, companyName) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, firstName, lastName, companyName })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      // Save to localStorage
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('organization', JSON.stringify(data.organization));

      // Update state
      setToken(data.token);
      setUser(data.user);
      setOrganization(data.organization);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Save to localStorage
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('organization', JSON.stringify(data.organization));

      // Update state
      setToken(data.token);
      setUser(data.user);
      setOrganization(data.organization);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('organization');
    setToken(null);
    setUser(null);
    setOrganization(null);
  };

  const value = {
    user,
    organization,
    token,
    loading,
    register,
    login,
    logout,
    isAuthenticated: !!token
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};